import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { Toasty, useAtomsToastManager } from '@opendoor/atoms/toast';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import * as Sentry from '@sentry/nextjs';
import {
  dehydrate,
  type DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { requestZeusFromServer } from 'components/api/zeusServerClient';
import { pushUrl, replaceUrl } from 'components/helpers/history';
import { ListingSortControl } from 'components/marketplace/browse/ListingSortControl';
import {
  marketplaceBrowseQueryOptions,
  type MarketplaceBrowseVariables,
} from 'components/marketplace/browse/marketplaceBrowse';
import { PopularMarkets } from 'components/marketplace/browse/PopularMarkets';
import { SegmentedControl } from 'components/marketplace/browse/SegmentedControl';
import { useRegionPaginationArm } from 'components/marketplace/browse/useRegionPaginationArm';
import { buildMarketplacePageKey } from 'components/marketplace/helpers/marketplacePageKey';
import { dollarsToCents } from 'components/marketplace/helpers/money';
import {
  buildPropertySearchEvent,
  PropertySearchTrigger,
} from 'components/marketplace/helpers/propertySearchEvent';
import type { ListingSortSlug } from 'components/marketplace/helpers/sortOptions';
import {
  buildMarketInfoFromMarket,
  buildMarketInfoFromZone,
  fetchActiveBuyMarkets,
  fetchMarketRouteData,
  fetchZoneRouteData,
  parseMarketParam,
} from 'components/marketplace/helpers/ssr';
import {
  toSearchAreaInput,
  useAvailableSoonCount,
} from 'components/marketplace/helpers/useAvailableSoonCount';
import { useAvailableSoonSegment } from 'components/marketplace/helpers/useAvailableSoonSegment';
import { useMapRefs } from 'components/marketplace/helpers/useMapRefs';
import { useMarketplaceFavorite } from 'components/marketplace/helpers/useMarketplaceFavorite';
import { useMarketplaceSearchController } from 'components/marketplace/helpers/useMarketplaceSearchController';
import { usePropertyViewSurveyTrigger } from 'components/marketplace/helpers/usePropertyViewSurveyTrigger';
import {
  buildAddressPreviewParams,
  buildQueryParams,
  hasHomesMarketSeoFilters,
  isUuid,
  normalizeSearchVertices,
  parseQueryParams,
  parseQueryParamsFromSearch,
  parseSavedSearchId,
  removeAddressPreviewParams,
  SAVED_SEARCH_ID_PARAM,
} from 'components/marketplace/helpers/useSearchQueries';
import type { PropertySearchQuery } from 'components/marketplace/helpers/useSearchQueries';
import {
  buildPayloadFromQuery,
  getInitialPropertyTypes,
  LISTINGS_PAGE_SIZE,
  useSearchState,
} from 'components/marketplace/helpers/useSearchState';
import { useViewerMarketplaceFavorites } from 'components/marketplace/helpers/useViewerMarketplaceFavorites';
import { pickHomesMarketHeroPhoto } from 'components/marketplace/homesMarketSeo';
import type { MarketplacePropertySuggestionFragment } from 'components/marketplace/map/__generated__/location-search.zeus';
import { MarketplaceViewportAreaNameDocument } from 'components/marketplace/map/__generated__/viewport-area-name.zeus';
import { MarketplaceMap } from 'components/marketplace/map/MarketplaceMap';
import { MarketplaceMapMarkers } from 'components/marketplace/map/MarketplaceMapMarkers';
import { MiniPropertyDetailPage } from 'components/marketplace/map/MiniPropertyDetailPage';
import { MobileHeaderFilters } from 'components/marketplace/map/MobileHeaderFilters';
import { MobileMiniPropertyDetailPage } from 'components/marketplace/map/MobileMiniPropertyDetailPage';
import { PropertyList, PropertyListRefreshOverlay } from 'components/marketplace/map/PropertyList';
import { QuickFilters } from 'components/marketplace/map/QuickFilters';
import { MapSearchFilters } from 'components/marketplace/map/SearchFilters';
import {
  type MapLocation,
  type MarketInfo,
  MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS,
  MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS,
} from 'components/marketplace/map/types';
import { useMarketplaceListingPreview } from 'components/marketplace/map/useMarketplaceListingPreview';
import { useMobileMiniPDPSwipeDismiss } from 'components/marketplace/map/useMobileMiniPDPSwipeDismiss';
import { getShapeFromMap } from 'components/marketplace/map/utils';
import {
  getViewportAreaName,
  getViewportAreaNameInput,
  getViewportCenter,
} from 'components/marketplace/map/viewportAreaName';
import { MarketplaceSeoHead } from 'components/marketplace/MarketplaceSeoHead';
import { createMarketplaceQueryCache } from 'components/marketplace/queryCache';
import { dollarToCentsString, toFilterString } from 'components/marketplace/search/filterHelpers';
import { DEFAULT_MARKET, getZoomForLocationType } from 'components/marketplace/search/utils';
import { AVAILABLE_SOON_DISCLOSURE } from 'components/marketplace/shared/availableSoon';
import {
  MOBILE_MINI_PDP_PANEL_TRANSITION_CLASS,
  SLIDE_PANEL_TRANSITION_CLASS,
} from 'components/marketplace/shared/panelTransitions';
import type { ActiveMarket } from 'components/marketplace/types';
import MarketplaceMapLayout from 'components/properties/shared/MarketplaceMapLayout';
import { useSmallerThanLG, useSmallerThanMD } from 'components/shared/hooks/useMediaQuery';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import { useObservability } from 'helpers/observability';
import type { MapInstance, ViewStateChangeEvent } from 'react-map-gl/mapbox';
import { useDebouncedCallback } from 'use-debounce';

const VIEWPORT_AREA_NAME_TIMEOUT_MS = 2_000;

const SearchErrorToast = ({
  hasError,
  clearError,
}: {
  hasError: boolean;
  clearError: () => void;
}) => {
  const toastManager = useAtomsToastManager();
  const toastManagerRef = useRef(toastManager);
  // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
  toastManagerRef.current = toastManager;
  const clearErrorRef = useRef(clearError);
  // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
  clearErrorRef.current = clearError;

  useEffect(() => {
    if (!hasError) {
      return;
    }
    toastManagerRef.current.add({
      title: 'We couldn’t update results. Please try again.',
      variant: 'error',
    });
    clearErrorRef.current();
  }, [hasError]);
  return null;
};

// mapbox merges a camera method's `eventData` into every event that method
// fires, so tagging our own `flyTo` lets `handleOnMoveEnd` tell the fly's
// trailing `moveend` apart from a user pan/zoom and skip it — otherwise the fly
// we run to center a freshly selected zone would immediately trigger a viewport
// search that clobbers that zone. This is robust to however many map instances
// echo the fly (and to a no-op fly firing no `moveend`); untagged moves (user
// gestures, the geolocate control) are unaffected and still refetch.
const ZONE_FLY_EVENT_DATA: { odSkipViewportSearch: true } = { odSkipViewportSearch: true };

const MarketplacePage: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  initialVariables,
  marketInfo,
  queryParams,
  activeMarkets,
  canonicalOverride,
  savedSearchId,
  initialZoneIds,
  initialViewportAreaName,
  ssrListingCount,
}) => {
  const { authentication, user } = useAuth();
  const { trackJourneyEvent } = useObservability();
  const isLoggedIn = authentication.state === 'authenticated';
  const sellMarket = marketInfo.identifier
    ? {
        identifier: marketInfo.identifier,
        displayName: marketInfo.displayName || marketInfo.name,
      }
    : null;
  const { mapRef } = useMapRefs();
  const isSmallerThanMD = useSmallerThanMD();
  const isSmallerThanLG = useSmallerThanLG();

  const searchState = useSearchState(queryParams);

  const {
    mapMarkers,
    listings,
    appliedSort,
    appliedAvailableSoon,
    appliedPage,
    isLoading,
    isLoadingMore,
    hasError,
    clearError,
    selectedLocation,
    setSelectedLocation,
    selectedAddress,
    setSelectedAddress,
    applyFilters,
    applyZoneSearch,
    applyViewport,
    applyPage,
    loadMore,
    applySavedSearch,
    getSaveSearchQuery,
    activeSavedSearch,
    markActiveSavedSearch,
    clearActiveSavedSearch,
    locationLabel,
    searchLocation,
  } = useMarketplaceSearchController({
    initialVariables,
    searchState,
    marketInfo,
    initialVertices: queryParams.vertices,
    initialZoneIds,
    initialViewportAreaName,
    // `ssid` survives the hard navigation a cross-market saved-search apply
    // takes, restoring the active-search state on the destination page.
    initialSavedSearchId: savedSearchId,
  });

  const pageCount = Math.ceil((listings?.totalCount ?? 0) / LISTINGS_PAGE_SIZE);

  // Built from the parsed URL rather than live filter state: `getCanonical()`
  // reports every filter including its defaults, which would stamp six
  // redundant params onto each page link and give the crawler a different URL
  // than the canonical one. Seeding is off because these render during SSR.
  const hrefForPage = useCallback(
    (page: number) =>
      appendSavedSearchId(
        buildCanonicalMarketPath(
          marketInfo.identifier ?? '',
          { ...queryParams, page },
          { seedFromUrl: false },
        ),
        savedSearchId,
      ),
    [marketInfo.identifier, queryParams, savedSearchId],
  );

  // Fires only once the page's results have landed: `applyPage` abandons
  // superseded and failed fetches, and click-intent semantics would inflate
  // depth for exactly the heavy users the metric exists for. A page turn
  // never changes totalCount, so the value captured at click time stays
  // truthful when the search resolves. Filters are deliberately absent: they
  // would come from draft state, which a page turn does not apply.
  const trackPageTurn = useCallback(
    (page: number) => {
      trackJourneyEvent(
        'marketplaceProperty',
        'propertySearch',
        buildPropertySearchEvent({
          trigger: PropertySearchTrigger.PAGE_CHANGE,
          filters: { minBeds: null, minBaths: null, minPrice: null, maxPrice: null },
          market: marketInfo.name,
          resultsCount: listings?.totalCount,
          sort: appliedSort,
          page,
        }),
      );
    },
    [trackJourneyEvent, appliedSort, marketInfo.name, listings?.totalCount],
  );

  const handlePageSelect = useCallback(
    (page: number) => {
      void applyPage(page).then((status) => {
        if (status === 'applied') {
          trackPageTurn(page);
        }
      });
    },
    [applyPage, trackPageTurn],
  );

  // Show more is the same page turn as the paginated nav: it loads
  // `nextPage` of the same search, so it reports the same trigger. nextPage
  // is the API's zero-based index; the event follows the one-based URL
  // convention, so it is reported as nextPage + 1.
  const handleShowMore = useCallback(() => {
    const nextPage = listings?.nextPage;
    if (nextPage == null) {
      return;
    }
    void loadMore().then((loaded) => {
      if (loaded) {
        trackPageTurn(nextPage + 1);
      }
    });
  }, [listings?.nextPage, loadMore, trackPageTurn]);

  // Set by search-running handlers and consumed by the results-settle effect
  // below, so `resultsCount` reflects the search the interaction ran rather
  // than the previous results still on screen. Starts null so hydration and
  // the SSR-seeded initial results never fire an event.
  const pendingSearchTriggerRef = useRef<PropertySearchTrigger | null>(null);

  // Read at rollback time so a pick that never landed is undone against the
  // ordering currently on screen, not the one captured when it was made.
  const appliedAvailableSoonRef = useRef(appliedAvailableSoon);
  const appliedSortRef = useRef(appliedSort);
  const segmentPickSeqRef = useRef(0);
  useEffect(() => {
    appliedAvailableSoonRef.current = appliedAvailableSoon;
  }, [appliedAvailableSoon]);
  // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
  appliedSortRef.current = appliedSort;

  const handleResetFilters = () => {
    searchState.resetFilters();
    clearActiveSavedSearch();
    // Reset runs the unfiltered search immediately so results never sit
    // desynced from the cleared modal state; the settle effect reports the
    // count this search actually produced. The state setters above do not land
    // before the search builds its payload, so the reset shape goes in as an
    // explicit override.
    pendingSearchTriggerRef.current = PropertySearchTrigger.RESET;
    void applyFilters({ ...searchState.resetQuery });
  };

  const handleApplySavedSearch = (savedSearch: Parameters<typeof applySavedSearch>[0]) => {
    pendingSearchTriggerRef.current = PropertySearchTrigger.SAVED_SEARCH;
    void applySavedSearch(savedSearch);
  };

  const {
    minBeds,
    minBaths,
    minPrice,
    maxPrice,
    singleFamily,
    condo,
    townhome,
    noHoas,
    availableSoon,
    singleStory,
    mustHavePool,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    minSquareFootage,
    maxSquareFootage,
  } = searchState.get;

  // Fires the pending propertySearch event once its results settle.
  // `listings.totalCount` still reflects the previous search at interaction
  // time (the fetch is async), so handlers stash the trigger and this effect
  // reports the count the interaction actually produced — once per settled
  // search, however many renders or debounced map moves preceded it.
  useEffect(() => {
    if (hasError) {
      pendingSearchTriggerRef.current = null;
      return;
    }
    const trigger = pendingSearchTriggerRef.current;
    if (trigger == null || isLoading || listings == null) {
      return;
    }
    pendingSearchTriggerRef.current = null;
    trackJourneyEvent(
      'marketplaceProperty',
      'propertySearch',
      buildPropertySearchEvent({
        trigger,
        filters: { minBeds, minBaths, minPrice, maxPrice },
        market: marketInfo.name,
        resultsCount: listings.totalCount,
        sort: appliedSort,
      }),
    );
  }, [
    appliedSort,
    hasError,
    isLoading,
    listings,
    minBeds,
    minBaths,
    minPrice,
    maxPrice,
    marketInfo.name,
    trackJourneyEvent,
  ]);

  // UX states
  const [errors, setErrors] = useState<Record<string, string>>();
  const [showList, setShowList] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showPdp, setShowPdp] = useState<boolean>(false);
  const customerId = user?.customerUuid;
  const { likedPropertyIds, initialFavoritesPending, onFavoriteChange } =
    useViewerMarketplaceFavorites(customerId, isLoggedIn);

  // Paginated URLs exist for everyone: the experiment decides which affordance a
  // visitor sees, not whether the pages are addressable. Only a settled `control`
  // falls back to Show more, so the server render — which is shared by every
  // visitor and by crawlers — always carries the page anchors.
  // Latched at landing. Reading `appliedPage` live would unenroll a visitor
  // the moment an in-place page turn lands — including one whose arm query
  // simply settled late — orphaning every page-turn event from any arm. The
  // exclusion is for arrivals past page 1, where Show more cannot represent
  // the loaded window, and a deep-linked arrival still mounts with
  // appliedPage > 1.
  const [eligibleAtLanding] = useState(
    () => Boolean(marketInfo.identifier) && pageCount > 1 && appliedPage === 1,
  );
  const paginationArm = useRegionPaginationArm({
    customerId,
    isAuthResolved: authentication.state !== 'authenticating',
    isEligible: eligibleAtLanding,
  });

  // Zone routes live at /homes/zid_<id>, which `hrefForPage` cannot address, so
  // they keep Show more until per-page zone URLs exist.
  const pagination =
    marketInfo.identifier && pageCount > 1 && paginationArm !== 'control'
      ? {
          kind: 'pages' as const,
          currentPage: appliedPage,
          totalPages: pageCount,
          hrefForPage,
          onPageSelect: handlePageSelect,
        }
      : { kind: 'showMore' as const };

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    queryParams.preview_aid ?? null,
  );
  const propertyPreview = useMarketplaceListingPreview(selectedAddressId);
  const {
    data: propertyDetails,
    isLoadingError: propertyPreviewLoadingFailed,
    isFetching: propertyPreviewIsFetching,
    isSuccess: propertyPreviewSucceeded,
    refetch: refetchPropertyPreview,
  } = propertyPreview;
  const loadingAddressId = propertyPreviewIsFetching ? selectedAddressId : null;
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: propertyDetails?.addressToken,
    initialFavoritePending: initialFavoritesPending,
    initialIsLiked: propertyDetails?.addressToken
      ? likedPropertyIds?.has(propertyDetails.addressToken)
      : undefined,
    origin: 'GALLERY',
    onFavoriteChange,
    priceCents:
      propertyDetails?.listPrice == null ? null : dollarsToCents(propertyDetails.listPrice),
  });
  const propertyDetailsWithFavorite = propertyDetails
    ? { ...propertyDetails, isFavoriteLoading, isLiked, onToggleFavorite }
    : null;
  const previewAddressId = propertyDetails?.addressToken;
  usePropertyViewSurveyTrigger({ addressToken: previewAddressId ?? null, isOpen: showPdp });
  const handleFetchPropertyDetails = useCallback(
    (addressId: string) => {
      if (addressId === selectedAddressId) {
        if (propertyPreviewLoadingFailed || (!propertyDetails && !propertyPreviewIsFetching)) {
          void refetchPropertyPreview();
        } else if (propertyDetails) {
          setShowPdp(true);
        }
        return;
      }
      setSelectedAddressId(addressId);
    },
    [
      propertyDetails,
      propertyPreviewLoadingFailed,
      propertyPreviewIsFetching,
      refetchPropertyPreview,
      selectedAddressId,
    ],
  );

  useEffect(() => {
    if (!selectedAddressId || previewAddressId !== selectedAddressId) {
      return;
    }
    // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
    setShowFilters(false);
    setShowPdp(true);
    trackJourneyEvent('marketplaceProperty', 'detailPageView', {
      addressUuid: previewAddressId,
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview_aid') !== previewAddressId) {
      const urlParams = buildAddressPreviewParams(previewAddressId);
      pushUrl(`${window.location.pathname}?${urlParams}`);
    }
  }, [previewAddressId, selectedAddressId, trackJourneyEvent]);

  useEffect(() => {
    const previewUnavailable =
      propertyPreviewLoadingFailed || (propertyPreviewSucceeded && !propertyDetails);
    if (selectedAddressId && !propertyPreviewIsFetching && previewUnavailable) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      setShowPdp(false);
    }
  }, [
    propertyDetails,
    propertyPreviewLoadingFailed,
    propertyPreviewIsFetching,
    propertyPreviewSucceeded,
    selectedAddressId,
  ]);

  const flyTo = (location: MapLocation) => {
    if (!location.coordinates) {
      return;
    }
    const zoom = getZoomForLocationType(location.zoneType);

    mapRef.current?.flyTo({ center: location.coordinates, zoom }, ZONE_FLY_EVENT_DATA);
  };

  const handleError = (key: string, val: string) => {
    if (val === '') {
      setErrors((previousErrors) => {
        if (!previousErrors?.[key]) {
          return previousErrors;
        }
        const nextErrors = { ...previousErrors };
        delete nextErrors[key];
        return Object.keys(nextErrors).length > 0 ? nextErrors : undefined;
      });
      return;
    }

    setErrors((previousErrors) => ({ ...previousErrors, [key]: val }));
  };

  useEffect(() => {
    if (marketInfo.identifier && !canonicalOverride) {
      // `queryParams` is frozen at SSR, so its page is the one the visitor
      // arrived on. Reading the live page keeps a re-run from replacing a paged
      // entry with the entry page, without making the page a dependency — that
      // would run this on every page change and fight `applyPage`'s push.
      const canonicalPath = buildCanonicalMarketPath(marketInfo.identifier, {
        ...queryParams,
        page: parseQueryParamsFromSearch(window.location.search).page,
      });
      if (`${window.location.pathname}${window.location.search}` !== canonicalPath) {
        replaceUrl(canonicalPath);
      }
    }
  }, [canonicalOverride, marketInfo.identifier, queryParams]);

  useEffect(() => {
    const handleHistoryChange = () => {
      const addressId = new URLSearchParams(window.location.search).get('preview_aid');
      setShowPdp(false);
      setSelectedAddressId(addressId && isUuid(addressId) ? addressId : null);
    };

    window.addEventListener('popstate', handleHistoryChange);
    return () => window.removeEventListener('popstate', handleHistoryChange);
  }, []);

  // Smaller filter menu on desktop that allows for immediate querying
  const handleQuickFilterChange = (bedrooms: string, bathrooms: string) => {
    handleSetBedrooms(bedrooms);
    handleSetBathrooms(bathrooms);
    pendingSearchTriggerRef.current = PropertySearchTrigger.QUICK_FILTER;
    void applyFilters({ bedrooms: Number(bedrooms), bathrooms: Number(bathrooms) });
  };

  // Mirrors handleSortChange's contract: the control follows the pending pick
  // for instant feedback, but a failed or superseded switch rolls back to the
  // segment of the results on screen. The guard is the pick's identity, not
  // its value — a re-pick of the same segment while an earlier search is still
  // in flight must not be stomped by that search's settle.
  const handleInventorySegmentChange = (segment: string) => {
    const nextAvailableSoon = segment === 'availableSoon';
    const pickSequence = ++segmentPickSeqRef.current;
    searchState.set.setAvailableSoon(nextAvailableSoon);
    pendingSearchTriggerRef.current = PropertySearchTrigger.QUICK_FILTER;
    void applyFilters({ availableSoon: nextAvailableSoon }).then((status) => {
      if (status === 'applied' || segmentPickSeqRef.current !== pickSequence) {
        return;
      }
      searchState.set.setAvailableSoon(appliedAvailableSoonRef.current);
    });
  };

  // Keep the control visible while an available-soon search is on screen even
  // if the last home lists mid-session, and show it to deep-link visitors when
  // the flag is off, so the way back never unmounts.
  const availableSoonSegment = useAvailableSoonSegment();
  // Market- or zone-level on purpose: whether an area offers coming-soon homes
  // doesn't change as the map pans across viewport vertices. Control-arm
  // visitors need the count too: exposure is bucketed on market eligibility,
  // which is arm-independent, so the read cannot bias the split.
  const { data: availableSoonCount } = useAvailableSoonCount({
    where: toSearchAreaInput({
      market: marketInfo.name,
      vertices: null,
      zoneIds: searchLocation.zoneIds,
    }),
    enabled: availableSoonSegment.enabled || availableSoonSegment.isExperimentAssignment,
  });
  const showInventorySegment =
    (availableSoonSegment.enabled || availableSoon) &&
    (availableSoon || (availableSoonCount ?? 0) > 0);

  // Exposed = the market offers coming-soon homes, so the segment could render.
  // Fires for both arms, latched per unit per session. The ?availableSoon=true
  // deep link can render the control in a zero-count market with no exposure
  // recorded — symmetric across arms, so the split stays unbiased.
  const { isExperimentAssignment, trackExposure } = availableSoonSegment;
  useEffect(() => {
    if (isExperimentAssignment && (availableSoonCount ?? 0) > 0) {
      trackExposure();
    }
  }, [isExperimentAssignment, availableSoonCount, trackExposure]);
  const inventorySegmentControl = showInventorySegment ? (
    <SegmentedControl
      aria-label="Listing availability"
      analyticsName="marketplace-inventory-segment"
      size="sm"
      value={availableSoon ? 'availableSoon' : 'forSale'}
      onValueChange={handleInventorySegmentChange}
    >
      <SegmentedControl.Item value="forSale">For sale</SegmentedControl.Item>
      <SegmentedControl.Item value="availableSoon">Available soon</SegmentedControl.Item>
    </SegmentedControl>
  ) : null;

  // The control renders `appliedSort`, so the displayed ordering only changes
  // when results land. A pick that never lands still has to be undone, or a
  // later unrelated search would inherit an ordering the user never got and
  // apply it as if the filter change had asked for it. `appliedSort` is the
  // only safe anchor: it always holds a landed value, so neither concurrent
  // picks nor a superseding search that itself fails can leave an intermediate
  // ordering behind.
  const handleSortChange = (nextSort: ListingSortSlug) => {
    if (nextSort === appliedSort && nextSort === searchState.get.sort) {
      return;
    }
    searchState.set.setSort(nextSort);
    pendingSearchTriggerRef.current = PropertySearchTrigger.SORT_CHANGE;
    void applyFilters({ sort: nextSort }).then((status) => {
      // A superseding search that carries this same sort commits it itself, so
      // by the time this resolves the ordering is already live and there is
      // nothing to undo. Rolling back on the stale closure value here would
      // revert a selection that did land.
      if (status === 'applied' || appliedSortRef.current === nextSort) {
        return;
      }
      searchState.set.setSort((current) =>
        current === nextSort ? appliedSortRef.current : current,
      );
    });
  };

  // Mobile filter menu CTA click handler to trigger a search, close the filter
  // menu, and fly to the selected location.
  const handleMobileFilterClick = () => {
    if (selectedLocation) {
      flyTo(selectedLocation);
    }
    pendingSearchTriggerRef.current = PropertySearchTrigger.FILTER_APPLY;
    void (async () => {
      await applyFilters();
      setShowFilters(false);
    })();
  };

  // Desktop full-filter "Search" CTA. Wraps `applyFilters` so the press event
  // isn't forwarded as a filter override.
  const handleApplyFilters = useCallback(() => {
    pendingSearchTriggerRef.current = PropertySearchTrigger.FILTER_APPLY;
    void applyFilters();
  }, [applyFilters]);

  const handleCloseFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  // The trigger is stashed here, inside the debounced apply, rather than in
  // `handleOnMoveEnd`, so a burst of map moves marks one pending search per
  // debounce settle instead of one per `moveend`.
  const setSearchAreaFromMapBounds = useCallback(
    (map: MapInstance) => {
      const shape = getShapeFromMap(map);
      // Mirrors applyViewport's no-op guard: arming the trigger for a search
      // that never runs would leave it to fire on an unrelated later change.
      if (normalizeSearchVertices(shape)) {
        pendingSearchTriggerRef.current = PropertySearchTrigger.MAP_MOVE;
      }
      return applyViewport(shape);
    },
    [applyViewport],
  );

  const closePropertyDetails = useCallback(() => {
    setShowPdp(false);
    setSelectedAddressId(null);
    const urlParams = removeAddressPreviewParams();
    replaceUrl(`${window.location.pathname}?${urlParams}`);
  }, []);

  const mobileMiniPdpSwipe = useMobileMiniPDPSwipeDismiss(
    closePropertyDetails,
    showPdp && Boolean(propertyDetails),
  );

  const debouncedSetSearchAreaFromViewport = useDebouncedCallback(setSearchAreaFromMapBounds, 200);

  const handleOnMoveEnd = useCallback(
    (e: ViewStateChangeEvent) => {
      // Skip the `moveend` from our own zone-centering fly (tagged in `flyTo`);
      // a user pan/zoom is untagged and still refetches the viewport.
      if ('odSkipViewportSearch' in e && e.odSkipViewportSearch === true) {
        return;
      }
      void debouncedSetSearchAreaFromViewport(e.target);
    },
    [debouncedSetSearchAreaFromViewport],
  );

  const handleSetBedrooms = (value: string) => searchState.set.setBedrooms(Number(value));
  const handleSetBathrooms = (value: string) => searchState.set.setBathrooms(Number(value));
  const handleMinPriceChange = (value: string) => searchState.set.setMinPrice(Number(value) / 100);
  const handleMaxPriceChange = (value: string) => searchState.set.setMaxPrice(Number(value) / 100);
  const handleSingleFamilyChange = (_: string, checked: string | boolean) =>
    searchState.set.setSingleFamily(!!checked);
  const handleCondoChange = (_: string, checked: string | boolean) =>
    searchState.set.setCondo(!!checked);
  const handleTownhomeChange = (_: string, checked: string | boolean) =>
    searchState.set.setTownhome(!!checked);
  const handleNoHoasChange = (_: string, checked: string | boolean) =>
    searchState.set.setNoHoas(!!checked);
  const handleSingleStoryChange = (_: string, checked: string | boolean) =>
    searchState.set.setSingleStory(!!checked);
  const handlePoolChange = (_: string, checked: string | boolean) =>
    searchState.set.setMustHavePool(!!checked);
  const handleMinYearBuiltChange = (value: string) =>
    searchState.set.setMinYearBuilt(Number(value));
  const handleMaxYearBuiltChange = (value: string) =>
    searchState.set.setMaxYearBuilt(Number(value));
  const handleMinLotSizeChange = (value: string) => searchState.set.setMinLotSize(Number(value));
  const handleMaxLotSizeChange = (value: string) => searchState.set.setMaxLotSize(Number(value));
  const handleMinSquareFootageChange = (min: string) =>
    searchState.set.setMinSquareFootage(Number(min));
  const handleMaxSquareFootageChange = (max: string) =>
    searchState.set.setMaxSquareFootage(Number(max));

  const handleShowList = () => {
    setShowList(true);
  };
  const handleShowMap = () => setShowList(false);
  const onFiltersButtonClick = () => {
    closePropertyDetails();
    setShowList(false);
    setShowPdp(false);
    setShowFilters(true);
  };

  const handleSetSelectedLocation = (location?: MapLocation) => {
    if (!location) {
      setSelectedLocation(undefined);
      return;
    }
    setIsMobileHeaderFocused(false);
    debouncedSetSearchAreaFromViewport.cancel();
    flyTo(location);
    // Mirrors applyZoneSearch's no-op guard (see setSearchAreaFromMapBounds).
    if (isUuid(location.value)) {
      pendingSearchTriggerRef.current = PropertySearchTrigger.LOCATION_SELECT;
    }
    void applyZoneSearch(location);
  };

  const handleSetSelectedAddress = (address?: MarketplacePropertySuggestionFragment) => {
    if (!address) {
      setSelectedAddress(undefined);
      return;
    }
    setSelectedAddress(address);
    handleFetchPropertyDetails(address.addressId);
    setIsMobileHeaderFocused(false);
    // Address selection previews a single listing without re-running the
    // browse search, so the on-screen count is still accurate here.
    trackJourneyEvent(
      'marketplaceProperty',
      'propertySearch',
      buildPropertySearchEvent({
        trigger: PropertySearchTrigger.LOCATION_SELECT,
        filters: { minBeds, minBaths, minPrice, maxPrice },
        market: marketInfo.name,
        resultsCount: listings?.totalCount,
        sort: searchState.get.sort,
      }),
    );
  };

  const handleOnMobileFilterClick = () => {
    closePropertyDetails();
    setShowList(false);
    setShowFilters(true);
  };

  const [isMobileHeaderFocused, setIsMobileHeaderFocused] = useState(false);

  const handleOnMobileHeaderFocus = () => {
    setIsMobileHeaderFocused(true);
  };

  const handleOnMobileHeaderBlur = () => {
    setIsMobileHeaderFocused(false);
  };

  const RenderBody = (
    <MarketplaceMapLayout hideFooter>
      <Toasty>
        <Col className="relative h-full w-full overflow-hidden border-t border-od-border-primary bg-od-bg-primary md:flex-row">
          {/** Header */}
          <Row
            onBlur={handleOnMobileHeaderBlur}
            onFocus={handleOnMobileHeaderFocus}
            className={cn(
              'fixed top-1.25 z-[8000] h-15 items-center justify-center transition-[width,left] duration-[250ms] md:hidden',
              isMobileHeaderFocused
                ? 'left-2.5 w-[95%]'
                : 'left-13.75 w-[calc(100%_-_var(--spacing)*50)]',
            )}
          >
            <Col className="w-full">
              <MobileHeaderFilters
                isFocused={isMobileHeaderFocused}
                setSelectedAddress={handleSetSelectedAddress}
                selectedAddress={selectedAddress}
                setSelectedLocation={handleSetSelectedLocation}
                selectedLocation={selectedLocation}
                onBack={handleOnMobileHeaderBlur}
                onOpenFilters={handleOnMobileFilterClick}
              />
            </Col>
          </Row>
          {/** Responsive map — one Mapbox instance for all breakpoints */}
          <Col
            aria-hidden={
              (isSmallerThanMD && (showList || showFilters)) ||
              (!isSmallerThanMD && isSmallerThanLG && showFilters)
            }
            className={cn(
              'relative h-screen w-full touch-none items-center border-t border-od-border-primary transition-[opacity,visibility] duration-500 md:h-full md:basis-1/2 lg:flex-1 lg:basis-1/2',
              (showList || showFilters) && 'max-md:invisible max-md:opacity-0',
              showFilters && 'md:invisible md:opacity-0 lg:visible lg:opacity-100',
            )}
          >
            <MarketplaceMap
              mapRef={mapRef}
              onClick={closePropertyDetails}
              onMoveEnd={handleOnMoveEnd}
              marketShape={marketInfo.shape}
            >
              <MarketplaceMapMarkers
                markers={mapMarkers}
                likedPropertyIds={likedPropertyIds}
                selectedAddressId={propertyDetails?.addressToken}
                loadingAddressId={loadingAddressId}
                onListingClick={handleFetchPropertyDetails}
              />
            </MarketplaceMap>
            <Button
              analyticsName="list-view"
              aria-label="List"
              onClick={handleShowList}
              size="lg"
              variant="secondaryInverse"
              className={cn(
                'fixed bottom-7.5 left-1/2 -translate-x-1/2 md:hidden',
                showList && 'hidden',
              )}
            >
              List
            </Button>
          </Col>
          {/** Fullscreen List (Small) */}
          <Col
            id="small-property-list"
            aria-hidden={!showList}
            className={cn(
              'absolute z-3 h-full w-full items-center overflow-y-scroll bg-od-bg-primary px-5 pb-32.5 md:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showList && 'invisible translate-y-[100vh] opacity-0',
            )}
          >
            <Col className="h-full w-full px-6">
              <Col className="flex-wrap justify-between pb-13">
                <Row className="items-center justify-between gap-4 py-6">
                  <Text
                    as="p"
                    aria-busy={(isLoading && listings != null) || undefined}
                    className="text-body-sm"
                  >
                    {listings?.totalCount ?? 0} homes
                  </Text>
                  <ListingSortControl
                    analyticsName="marketplace-sort-mobile"
                    className="w-44 shrink-0"
                    value={appliedSort}
                    onChange={handleSortChange}
                  />
                </Row>
                {showInventorySegment ? (
                  <Row className="justify-center pb-4">{inventorySegmentControl}</Row>
                ) : null}
                {appliedAvailableSoon ? (
                  <Text as="p" className="pb-4 text-body-sm text-od-content-tertiary">
                    {AVAILABLE_SOON_DISCLOSURE}
                  </Text>
                ) : null}
                <PropertyListRefreshOverlay isRefreshing={isLoading && listings != null}>
                  <PropertyList
                    isLoading={isLoading}
                    isLoadingMore={isLoadingMore}
                    handleOnSeeMore={handleShowMore}
                    listings={listings}
                    likedPropertyIds={likedPropertyIds}
                    initialFavoritesPending={initialFavoritesPending}
                    onFavoriteChange={onFavoriteChange}
                    firstPhotoLoadMode="deferred"
                    pagination={pagination}
                  />
                </PropertyListRefreshOverlay>
                <PopularMarkets
                  markets={activeMarkets}
                  sellMarket={sellMarket}
                  className="pt-12 pb-27.5"
                />
              </Col>
            </Col>
            <Button
              analyticsName="list-view"
              aria-label="Map"
              onClick={handleShowMap}
              size="lg"
              variant="secondaryInverse"
              className={cn('fixed bottom-7.5 left-1/2 -translate-x-1/2', !showList && 'hidden')}
            >
              Map
            </Button>
          </Col>
          {/** Side List (Medium/Large) */}
          <Col
            id="property-list-container"
            aria-hidden={showPdp || showFilters}
            className={cn(
              'relative min-w-0 basis-1/2 border-t border-od-border-primary bg-od-bg-primary transition-[opacity,visibility] duration-500 max-md:hidden',
              (showPdp || showFilters) && 'invisible opacity-0',
            )}
          >
            <Col className="z-1 h-full w-full overflow-y-scroll px-6 pt-3 lg:px-12">
              <QuickFilters
                bedrooms={String(minBeds)}
                bathrooms={String(minBaths)}
                selectedAddress={selectedAddress}
                setSelectedAddress={handleSetSelectedAddress}
                setSelectedLocation={handleSetSelectedLocation}
                selectedLocation={selectedLocation}
                onFilterChange={handleQuickFilterChange}
                onFiltersButtonClick={onFiltersButtonClick}
                isFocused={isMobileHeaderFocused}
                onApplySavedSearch={handleApplySavedSearch}
                activeSavedSearchId={activeSavedSearch?.id}
                inventoryControl={inventorySegmentControl}
              />
              <Row className="items-center justify-between gap-4 pb-6">
                <Text
                  as="p"
                  aria-busy={(isLoading && listings != null) || undefined}
                  className="min-w-0 text-body-sm"
                >
                  {listings?.totalCount ?? 0}{' '}
                  {appliedAvailableSoon ? 'homes available soon' : 'homes for sale'} near{' '}
                  {locationLabel}
                </Text>
                <ListingSortControl
                  analyticsName="marketplace-sort-desktop"
                  className="w-44 shrink-0"
                  value={appliedSort}
                  onChange={handleSortChange}
                />
              </Row>
              {appliedAvailableSoon ? (
                <Text as="p" className="pb-6 text-body-sm text-od-content-tertiary">
                  {AVAILABLE_SOON_DISCLOSURE}
                </Text>
              ) : null}
              <PropertyListRefreshOverlay isRefreshing={isLoading && listings != null}>
                <PropertyList
                  isLoading={isLoading}
                  isLoadingMore={isLoadingMore}
                  handleOnSeeMore={handleShowMore}
                  listings={listings}
                  likedPropertyIds={likedPropertyIds}
                  initialFavoritesPending={initialFavoritesPending}
                  onFavoriteChange={onFavoriteChange}
                  firstPhotoLoadMode="priority"
                  pagination={pagination}
                />
              </PropertyListRefreshOverlay>
              <PopularMarkets
                markets={activeMarkets}
                sellMarket={sellMarket}
                className="pt-12 pb-25"
              />
            </Col>
          </Col>
          {/** Fullscreen Filters (Small/Medium) */}
          <Col
            id="small-medium-filters"
            aria-hidden={!showFilters}
            className={cn(
              'fixed -top-1.25 z-[15000] h-full w-full items-center bg-od-bg-primary lg:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showFilters && 'invisible -translate-y-[100vh] opacity-0',
            )}
          >
            <MapSearchFilters
              minBeds={toFilterString(minBeds)}
              minBaths={toFilterString(minBaths)}
              minPrice={dollarToCentsString(minPrice)}
              maxPrice={dollarToCentsString(maxPrice)}
              singleFamily={singleFamily}
              condo={condo}
              townhome={townhome}
              noHoas={noHoas}
              singleStory={singleStory}
              mustHavePool={mustHavePool}
              minYearBuilt={toFilterString(minYearBuilt)}
              maxYearBuilt={toFilterString(maxYearBuilt)}
              minLotSize={toFilterString(minLotSize)}
              maxLotSize={toFilterString(maxLotSize)}
              minSquareFootage={toFilterString(minSquareFootage)}
              maxSquareFootage={toFilterString(maxSquareFootage)}
              handleError={handleError}
              handleMinBedsChange={handleSetBedrooms}
              handleMinBathsChange={handleSetBathrooms}
              handleMinSquareFootageChange={handleMinSquareFootageChange}
              handleMaxSquareFootageChange={handleMaxSquareFootageChange}
              handleMinPriceChange={handleMinPriceChange}
              handleMaxPriceChange={handleMaxPriceChange}
              handleSingleFamilyChange={handleSingleFamilyChange}
              handleCondoChange={handleCondoChange}
              handleTownhomeChange={handleTownhomeChange}
              handleNoHoasChange={handleNoHoasChange}
              handleSingleStoryChange={handleSingleStoryChange}
              handlePoolChange={handlePoolChange}
              handleMinYearBuiltChange={handleMinYearBuiltChange}
              handleMaxYearBuiltChange={handleMaxYearBuiltChange}
              handleMinLotSizeChange={handleMinLotSizeChange}
              handleMaxLotSizeChange={handleMaxLotSizeChange}
              onApplyFilters={handleMobileFilterClick}
              onResetFilters={handleResetFilters}
              onClose={handleCloseFilters}
              isLoading={isLoading}
              errors={errors}
              saveSearchQuery={availableSoon ? undefined : getSaveSearchQuery()}
              onApplySavedSearch={handleApplySavedSearch}
              activeSavedSearch={activeSavedSearch}
              locationLabel={selectedLocation?.label}
              onSavedSearchCreated={markActiveSavedSearch}
            />
          </Col>
          {/** Side Filters (Large) */}
          <Col
            id="pdp-filters-container"
            aria-hidden={!showFilters}
            aria-label="Search filters"
            as="aside"
            className={cn(
              'absolute right-0 z-2 h-full w-1/2 border-t border-od-border-primary bg-od-bg-primary max-lg:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showFilters && 'invisible translate-x-[50vh] opacity-0',
            )}
          >
            <MapSearchFilters
              minBeds={toFilterString(minBeds)}
              minBaths={toFilterString(minBaths)}
              minPrice={dollarToCentsString(minPrice)}
              maxPrice={dollarToCentsString(maxPrice)}
              singleFamily={singleFamily}
              condo={condo}
              townhome={townhome}
              noHoas={noHoas}
              singleStory={singleStory}
              mustHavePool={mustHavePool}
              minYearBuilt={toFilterString(minYearBuilt)}
              maxYearBuilt={toFilterString(maxYearBuilt)}
              minLotSize={toFilterString(minLotSize)}
              maxLotSize={toFilterString(maxLotSize)}
              minSquareFootage={toFilterString(minSquareFootage)}
              maxSquareFootage={toFilterString(maxSquareFootage)}
              handleError={handleError}
              handleMinBedsChange={handleSetBedrooms}
              handleMinBathsChange={handleSetBathrooms}
              handleMinSquareFootageChange={handleMinSquareFootageChange}
              handleMaxSquareFootageChange={handleMaxSquareFootageChange}
              handleMinPriceChange={handleMinPriceChange}
              handleMaxPriceChange={handleMaxPriceChange}
              handleSingleFamilyChange={handleSingleFamilyChange}
              handleCondoChange={handleCondoChange}
              handleTownhomeChange={handleTownhomeChange}
              handleNoHoasChange={handleNoHoasChange}
              handleSingleStoryChange={handleSingleStoryChange}
              handlePoolChange={handlePoolChange}
              handleMinYearBuiltChange={handleMinYearBuiltChange}
              handleMaxYearBuiltChange={handleMaxYearBuiltChange}
              handleMinLotSizeChange={handleMinLotSizeChange}
              handleMaxLotSizeChange={handleMaxLotSizeChange}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              onClose={handleCloseFilters}
              isLoading={isLoading}
              errors={errors}
              saveSearchQuery={availableSoon ? undefined : getSaveSearchQuery()}
              onApplySavedSearch={handleApplySavedSearch}
              activeSavedSearch={activeSavedSearch}
              locationLabel={selectedLocation?.label}
              onSavedSearchCreated={markActiveSavedSearch}
            />
          </Col>
          {/** Mini PDP (Medium/Large) */}
          <Col
            aria-hidden={!showPdp}
            className={cn(
              'absolute right-0 z-2 h-full w-1/2 overflow-y-scroll bg-od-bg-primary max-md:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showPdp && 'invisible translate-x-[50vh] opacity-0',
            )}
          >
            {propertyDetailsWithFavorite && (
              <MiniPropertyDetailPage
                property={propertyDetailsWithFavorite}
                onClose={closePropertyDetails}
                key={propertyDetailsWithFavorite.addressToken}
              />
            )}
          </Col>
          {/** Mobile Mini PDP (Small) */}
          <Col
            // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
            ref={mobileMiniPdpSwipe.panelRef}
            aria-hidden={!showPdp}
            className={cn(
              'absolute bottom-0 left-2.5 right-2.5 z-[3000] overflow-hidden rounded-t-4xl bg-od-bg-primary md:hidden',
              MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS,
              MOBILE_MINI_PDP_PANEL_TRANSITION_CLASS,
              !showPdp && cn('invisible opacity-0', MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS),
              // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
              mobileMiniPdpSwipe.panelClassName,
            )}
          >
            {propertyDetails && (
              <MobileMiniPropertyDetailPage
                property={propertyDetails}
                onClose={closePropertyDetails}
              />
            )}
          </Col>
        </Col>
        <SearchErrorToast hasError={hasError} clearError={clearError} />
      </Toasty>
    </MarketplaceMapLayout>
  );

  return (
    <>
      <MarketplaceSeoHead
        marketInfo={marketInfo}
        activeMarkets={activeMarkets}
        canonicalOverride={canonicalOverride ?? undefined}
        listingCount={ssrListingCount}
        heroPhotoUrl={pickHomesMarketHeroPhoto(
          listings?.listings.flatMap((listing) => listing.photoUrls ?? []) ?? [],
        )}
      />
      {RenderBody}
    </>
  );
};

const NOT_FOUND = { notFound: true } as const;

// Map of query params to their canonical path overrides
const CANONICAL_PATH_OVERRIDES: Record<string, string> = {
  is_near_me_page: '/homes-for-sale-near-me',
};

/**
 * `parseQueryParams` drops `ssid`, so a page link built from parsed params loses
 * it. Re-appending keeps the anchor's address equal to the one a click pushes,
 * and keeps a reload on that link restoring the active saved search.
 */
const appendSavedSearchId = (path: string, savedSearchId: string | null): string => {
  if (savedSearchId == null || savedSearchId === '') {
    return path;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${SAVED_SEARCH_ID_PARAM}=${encodeURIComponent(savedSearchId)}`;
};

export const buildCanonicalMarketPath = (
  marketIdentifier: string,
  query: PropertySearchQuery,
  options?: { seedFromUrl?: boolean },
): string => {
  const params = buildQueryParams({ ...query, market: null }, options);
  const queryString = params.toString();
  return `/homes/${marketIdentifier}${queryString ? `?${queryString}` : ''}`;
};

export const getServerSideProps: CacheSafeGetServerSideProps<{
  dehydratedState: DehydratedState;
  initialVariables: MarketplaceBrowseVariables;
  marketInfo: MarketInfo;
  queryParams: PropertySearchQuery;
  activeMarkets: ActiveMarket[];
  canonicalOverride: string | null;
  savedSearchId: string | null;
  initialZoneIds: string[] | null;
  initialViewportAreaName: string | null;
  ssrListingCount: number | null;
}> = async (context) => {
  const queryParams = parseQueryParams(context.query);
  const savedSearchId = parseSavedSearchId(context.query);
  const route = parseMarketParam(queryParams.market);
  // Match SSR search precedence (vertices > zoneIds): a zid route that also
  // carries a viewport polygon must not seed routeZoneIds, or the controller
  // would force vertices to null on the next client search/save.
  const hasViewportPolygon = (queryParams.vertices?.length ?? 0) > 0;
  let initialZoneIds: string[] | null = null;
  if (!hasViewportPolygon) {
    initialZoneIds = route.kind === 'zone' ? [route.zoneId] : (queryParams.zoneIds ?? null);
  }

  // Check for canonical path overrides based on query params from cloudflare routing
  const overrideKey = Object.keys(CANONICAL_PATH_OVERRIDES).find(
    (key) => context.query[key] === 'true',
  );
  const canonicalOverride = overrideKey ? CANONICAL_PATH_OVERRIDES[overrideKey] : null;

  let activeMarketsPromise: Promise<ActiveMarket[]> = Promise.resolve([]);
  let marketInfo: MarketInfo | false = false;
  let resolvedMarket: string | null = null;

  if (route.kind === 'market') {
    const { market } = route;
    resolvedMarket = market.toLowerCase();
    activeMarketsPromise = fetchActiveBuyMarkets(context.req.headers);
    const routeData = await fetchMarketRouteData(market, context.req.headers);
    const requestedMarket = routeData.marketplace.market;
    const skipMarketFallback = context.query.skip_market_fallback === 'true';
    if (
      (!requestedMarket || !requestedMarket.active) &&
      !skipMarketFallback &&
      market.toLowerCase() !== DEFAULT_MARKET
    ) {
      return {
        redirect: {
          destination: `/homes/${DEFAULT_MARKET}`,
          permanent: false,
        },
      };
    }
    if (!requestedMarket) {
      return NOT_FOUND;
    }
    resolvedMarket = requestedMarket.identifier;
    marketInfo = buildMarketInfoFromMarket(requestedMarket, queryParams);
  } else if (route.kind === 'zone') {
    activeMarketsPromise = fetchActiveBuyMarkets(context.req.headers);
    const routeData = await fetchZoneRouteData(route.zoneId, context.req.headers);
    if (!routeData.marketplace.zone) {
      return NOT_FOUND;
    }
    marketInfo = buildMarketInfoFromZone(routeData.marketplace.zone, queryParams);
  } else {
    return NOT_FOUND;
  }

  if (!marketInfo) {
    return NOT_FOUND;
  }

  const initialVariables = buildPayloadFromQuery(
    { ...queryParams, ...getInitialPropertyTypes(queryParams) },
    {
      market: route.kind === 'market' ? resolvedMarket : null,
      vertices: hasViewportPolygon ? queryParams.vertices : null,
      zoneIds: hasViewportPolygon ? null : initialZoneIds,
    },
  );
  const viewportCenter = hasViewportPolygon ? getViewportCenter(queryParams.vertices ?? []) : null;
  const fetchViewportAreaName = async (): Promise<string | null> => {
    if (!viewportCenter) {
      return null;
    }
    try {
      const response = await requestZeusFromServer(
        MarketplaceViewportAreaNameDocument,
        { input: getViewportAreaNameInput(viewportCenter) },
        {
          incomingRequestHeaders: context.req.headers,
          signal: AbortSignal.timeout(VIEWPORT_AREA_NAME_TIMEOUT_MS),
        },
      );
      return getViewportAreaName(response);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-browse', operation: 'viewportAreaName' },
      });
      return null;
    }
  };
  const queryClient = new QueryClient();
  let activeMarkets: ActiveMarket[];
  let initialViewportAreaName: string | null;
  let lastListingPage = 1;
  let ssrListingCount: number | null = null;
  try {
    const [browse, markets, areaName] = await Promise.all([
      queryClient.fetchInfiniteQuery(
        marketplaceBrowseQueryOptions({
          variables: initialVariables,
          request: (document, variables, signal) =>
            requestZeusFromServer(document, variables, {
              incomingRequestHeaders: context.req.headers,
              signal,
            }),
        }),
      ),
      activeMarketsPromise,
      fetchViewportAreaName(),
    ]);
    activeMarkets = markets;
    initialViewportAreaName = areaName;
    const totalCount = browse.pages[0]?.listings.totalCount ?? 0;
    lastListingPage = Math.max(1, Math.ceil(totalCount / LISTINGS_PAGE_SIZE));
    if (!hasHomesMarketSeoFilters(queryParams) && totalCount > 0) {
      ssrListingCount = totalCount;
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'marketplace-browse', operation: 'initialBrowse' },
    });
    throw error;
  }

  // A page past the end is either a probe or a link indexed before inventory
  // shrank, and neither is worth guessing an intent for. Sending it to the first
  // page matches what an unusable `page` already does — 0, a negative, and a
  // non-number all normalize away — instead of leaving an empty result set
  // indexable.
  const requestedPage = queryParams.page;
  if (
    requestedPage != null &&
    requestedPage > lastListingPage &&
    route.kind === 'market' &&
    resolvedMarket != null
  ) {
    return {
      redirect: {
        // No URL seeding: `buildQueryParams` reads `window.location` when it
        // seeds, and there is no window here.
        destination: buildCanonicalMarketPath(
          resolvedMarket,
          { ...queryParams, page: null },
          { seedFromUrl: false },
        ),
        permanent: false,
      },
    };
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      initialVariables,
      marketInfo,
      queryParams,
      activeMarkets,
      canonicalOverride,
      savedSearchId,
      initialZoneIds,
      initialViewportAreaName,
      ssrListingCount,
    },
  };
};

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
        queryCache: createMarketplaceQueryCache(),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={props.dehydratedState}>
        <MarketplacePage key={buildMarketplacePageKey(props)} {...props} />
      </HydrationBoundary>
    </QueryClientProvider>
  );
};

export default Page;
